// ============================================================================
// VINStep.jsx - Enhanced with VIN Barcode Scanning & Mismatch Handling
// ============================================================================
// Features:
// - Manual VIN entry (desktop/mobile)
// - Camera barcode scanning (mobile only)
// - VIN mismatch detection with options to reject or update
// - Supports Code 39 and Code 128 (standard VIN barcode formats)
// ============================================================================

import React, { Component } from 'react';
import { HTTPService } from "./../../../services/httpService";
import VINScanner from './VINScanner';
import { isPlaceholderVin } from '../../../util/assetType';

const API = new HTTPService();

class VINStep extends Component {
  constructor(props) {
    super(props);

    // Detect if mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    // Check if VIN is system-generated (NV / OT / BT)
    const isSystemVIN = isPlaceholderVin(props.vin);

    this.state = {
      vin: isSystemVIN ? '' : (props.vin || ''), // Don't show system-generated VINs
      vinConfirmed: false,
      loading: false,
      error: null,

      // Scanner state
      isMobile: isMobile,
      showScanner: false,
      scannerAvailable: false,

      // Mismatch state
      showMismatch: false,
      mismatchData: null,  // { expected: {...}, decoded: {...}, comparison: {...} }

      // Duplicate VIN state
      showDuplicateVin: false,
      duplicateVinData: null  // { conflictingJob: { requestId, vehicleYear, vehicleMake, vehicleModel, status, customerName } }
    };

    this.handleVINChange = this.handleVINChange.bind(this);
    this.handleVerifyVIN = this.handleVerifyVIN.bind(this);
    this.handleVINScanned = this.handleVINScanned.bind(this);
  }

  componentDidMount() {
    // Only check camera availability on mobile devices
    if (this.state.isMobile) {
      this.checkScannerAvailability();
    }
  }

  checkScannerAvailability = async () => {
    try {
      // Check if getUserMedia is supported
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        // Try to get camera permissions (doesn't actually start camera)
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        stream.getTracks().forEach(track => track.stop()); // Immediately stop

        this.setState({ scannerAvailable: true });
      }
    } catch (error) {
      console.log('Camera not available:', error);
      this.setState({ scannerAvailable: false });
    }
  };

  handleVINChange(event) {
    const vin = event.target.value.toUpperCase();
    this.setState({
      vin: vin,
      error: null,
      showMismatch: false,
      mismatchData: null,
      showDuplicateVin: false,
      duplicateVinData: null
    });
  }

  async handleVINScanned(scannedVIN) {
    console.log("📸 VIN scanned:", scannedVIN);

    this.setState({
      vin: scannedVIN.toUpperCase(),
      showScanner: false,
      error: null,
      showMismatch: false,
      mismatchData: null,
      showDuplicateVin: false,
      duplicateVinData: null,
      loading: true
    });

    // Automatically verify the scanned VIN
    await this.verifyVIN(scannedVIN.toUpperCase(), false);
  }

  openScanner = () => {
    this.setState({ showScanner: true });
  };

  closeScanner = () => {
    this.setState({ showScanner: false });
  };

  async handleVerifyVIN() {
    const { vin } = this.state;

    // Validate VIN length
    if (vin.length !== 17) {
      this.setState({ error: 'VIN must be exactly 17 characters' });
      return;
    }

    // Basic VIN validation (alphanumeric, no I, O, Q)
    const vinPattern = /^[A-HJ-NPR-Z0-9]{17}$/;
    if (!vinPattern.test(vin)) {
      this.setState({ error: 'Invalid VIN format. VIN cannot contain I, O, or Q.' });
      return;
    }

    await this.verifyVIN(vin, false);
  }

  async verifyVIN(vin, forceUpdate = false, vinOnly = false, decodedRef = null, skipDuplicateCheck = false) {
    try {
      this.setState({ loading: true, error: null });

      console.log("🔍 Verifying VIN:", vin, forceUpdate ? "(force update)" : "", vinOnly ? "(vin only)" : "");

      const payload = {
        vin: vin,
        vin_confirmed: true,
        force_update: forceUpdate,
        vin_only: vinOnly,
        skip_duplicate_check: skipDuplicateCheck
      };

      if (forceUpdate && decodedRef) {
        payload.decoded_ref = {
          make: decodedRef.make,
          model: decodedRef.model,
          trim: decodedRef.trim,
          id_car_make: decodedRef.id_car_make,
          id_car_model: decodedRef.id_car_model,
          id_car_trim: decodedRef.id_car_trim
        };
      }

      // Call backend to verify VIN
      const { data } = await API.post(
        `/requests/${this.props.requestId}/vin/verify`,
        payload
      );

      // Check for duplicate VIN in shop
      if (!data.success && data.duplicateVin) {
        console.log("⚠️ Duplicate VIN detected:", data);
        this.setState({
          loading: false,
          showDuplicateVin: true,
          duplicateVinData: {
            conflictingJob: data.conflictingJob
          }
        });
        return;
      }

      // Check for mismatch response
      if (!data.success && data.mismatch) {
        console.log("⚠️ VIN mismatch detected:", data);
        this.setState({
          loading: false,
          showMismatch: true,
          mismatchData: {
            expected: data.expected,
            decoded: data.decoded,
            comparison: data.comparison
          }
        });
        return;
      }

      if (!data.success) {
        throw new Error(data.error || 'Failed to verify VIN');
      }

      console.log("✅ VIN verified successfully", data.wasUpdated ? "(vehicle updated)" : "");

      this.setState({
        vinConfirmed: true,
        loading: false,
        showMismatch: false,
        mismatchData: null
      });

      // Notify parent component
      this.props.onVINVerified(vin);

    } catch (error) {
      console.error("❌ Error verifying VIN:", error);
      this.setState({
        loading: false,
        error: error.message
      });
    }
  }

  // Handle "Update Request" - accept mismatched VIN and update vehicle info
  handleUpdateRequest = async () => {
    const { vin, mismatchData } = this.state;
    console.log("📝 User chose to update request with new VIN");
    await this.verifyVIN(vin, true, false, mismatchData.decoded);
  };

  // Handle "Keep Original" - accept the VIN but keep original vehicle details
  handleKeepOriginal = async () => {
    const { vin } = this.state;
    console.log("📝 User chose to keep original vehicle, VIN only update");
    await this.verifyVIN(vin, false, true); // Re-submit with vin_only: true
  };

  // Handle "Go Back" - return to VIN entry
  handleCancelMismatch = () => {
    this.setState({
      showMismatch: false,
      mismatchData: null,
      vin: '',
      error: null
    });
  };

  // Handle "Proceed Anyway" - skip duplicate VIN check and continue
  handleProceedDuplicate = async () => {
    const { vin } = this.state;
    console.log("📝 User chose to proceed despite duplicate VIN");
    this.setState({ showDuplicateVin: false, duplicateVinData: null });
    await this.verifyVIN(vin, false, false, null, true);
  };

  // Handle "Go Back" from duplicate VIN warning
  handleCancelDuplicate = () => {
    this.setState({
      showDuplicateVin: false,
      duplicateVinData: null,
      vin: '',
      error: null
    });
  };

  // Render duplicate VIN warning UI
  renderDuplicateVinUI() {
    const { duplicateVinData } = this.state;
    if (!duplicateVinData) return null;

    const job = duplicateVinData.conflictingJob;

    return (
      <div className="vin-duplicate">
        <div className="alert alert-danger mb-4">
          <h5 className="alert-heading">
            <i className="fa-sharp fa-regular fa-car-crash mr-2"></i>
            Vehicle Already In Shop
          </h5>
          <p className="mb-0">
            A vehicle with this VIN is already checked in or in progress.
          </p>
        </div>

        {/* Conflicting Job Details */}
        <div className="card border-danger mb-4">
          <div className="card-header bg-danger text-white py-2">
            <small>
              <i className="fa-sharp fa-regular fa-exclamation-circle mr-1"></i>
              Existing Active Job
            </small>
          </div>
          <div className="card-body py-3">
            <h5 className="card-title mb-1">
              {job.vehicleYear} {job.vehicleMake} {job.vehicleModel}
            </h5>
            {job.customerName && (
              <p className="card-text text-muted mb-1 small">
                <i className="fa-sharp fa-regular fa-user mr-1"></i>
                {job.customerName}
              </p>
            )}
            <p className="card-text mb-0">
              <span className={`badge badge-${job.status === 'Checked In' ? 'info' : 'warning'}`}>
                {job.status}
              </span>
              {job.requestId && (
                <span className="text-muted ml-2 small">Job #{job.requestId}</span>
              )}
            </p>
          </div>
        </div>

        <p className="text-muted small text-center mb-4">
          This usually means the VIN was entered incorrectly, or the other job needs to be
          completed or cancelled first. If you're sure this is correct, you can proceed anyway.
        </p>

        {/* Action Buttons */}
        <div className="d-flex justify-content-center">
          <button
            className="btn btn-outline-secondary mr-3"
            onClick={this.handleCancelDuplicate}
          >
            <i className="fa-sharp fa-regular fa-arrow-left mr-1"></i>
            Re-enter VIN
          </button>
          <button
            className="btn btn-warning"
            onClick={this.handleProceedDuplicate}
            disabled={this.state.loading}
          >
            {this.state.loading ? (
              <i className="fa-sharp fa-regular fa-spinner fa-spin mr-1"></i>
            ) : (
              <i className="fa-sharp fa-regular fa-exclamation-triangle mr-1"></i>
            )}
            Proceed Anyway
          </button>
        </div>
      </div>
    );
  }

  // Render mismatch UI
  renderMismatchUI() {
    const { mismatchData } = this.state;
    if (!mismatchData) return null;

    const { expected, decoded, comparison } = mismatchData;

    return (
      <div className="vin-mismatch">
        <div className="alert alert-warning mb-4">
          <h5 className="alert-heading">
            <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2"></i>
            Vehicle Mismatch Detected
          </h5>
          <p className="mb-0">
            The VIN entered does not match the expected vehicle for this job.
          </p>
        </div>

        {/* Comparison Details */}
        <div className="card bg-light mb-3">
          <div className="card-body py-2">
            <h6 className="card-title mb-1">Differences:</h6>
            <ul className="mb-0 small">
              {!comparison.yearMatch && (
                <li>
                  <strong>Year:</strong> Expected {expected.year}, Actual {decoded.year}
                </li>
              )}
              {!comparison.makeMatch && (
                <li>
                  <strong>Make:</strong> Expected {expected.make}, Actual {decoded.make}
                </li>
              )}
              {!comparison.modelMatch && (
                <li>
                  <strong>Model:</strong> Expected {expected.model}, Actual {decoded.model}
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="row mb-3">
          {/* Expected Vehicle */}
          <div className="col-md-6 mb-3 mb-md-0">
            <div className="card border-secondary">
              <div className="card-header bg-secondary text-white py-2">
                <small>
                  <i className="fa-sharp fa-regular fa-file-alt mr-1"></i>
                  On File (From Request)
                </small>
              </div>
              <div className="card-body py-3">
                <h5 className="card-title mb-1">
                  {expected.year} {expected.make} {expected.model}
                </h5>
                {expected.trim && (
                  <p className="card-text text-muted mb-0 small">{expected.trim}</p>
                )}
              </div>
              <div className="card-footer bg-white text-center py-2">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={this.handleKeepOriginal}
                  disabled={this.state.loading}
                >
                  {this.state.loading ? (
                    <i className="fa-sharp fa-regular fa-spinner fa-spin mr-1"></i>
                  ) : (
                    <i className="fa-sharp fa-regular fa-car mr-1"></i>
                  )}
                  Keep This
                </button>
              </div>
            </div>
          </div>

          {/* Decoded Vehicle */}
          <div className="col-md-6">
            <div className="card border-primary">
              <div className="card-header bg-primary text-white py-2">
                <small>
                  <i className="fa-sharp fa-regular fa-car mr-1"></i>
                  Actual (From VIN)
                </small>
              </div>
              <div className="card-body py-3">
                <h5 className="card-title mb-1">
                  {decoded.year} {decoded.make} {decoded.model}
                </h5>
                {decoded.trim && (
                  <p className="card-text text-muted mb-0 small">{decoded.trim}</p>
                )}
              </div>
              <div className="card-footer bg-white text-center py-2">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={this.handleUpdateRequest}
                  disabled={this.state.loading}
                >
                  {this.state.loading ? (
                    <i className="fa-sharp fa-regular fa-spinner fa-spin mr-1"></i>
                  ) : (
                    <i className="fa-sharp fa-regular fa-check-circle mr-1"></i>
                  )}
                  Use This
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Go Back */}
        <div className="text-center">
          <button
            className="btn btn-link btn-sm text-muted"
            onClick={this.handleCancelMismatch}
          >
            <i className="fa-sharp fa-regular fa-arrow-left mr-1"></i>
            Re-enter VIN
          </button>
        </div>
      </div>
    );
  }

  render() {
    // Show scanner fullscreen
    if (this.state.showScanner) {
      return (
        <VINScanner
          onVINScanned={this.handleVINScanned}
          onClose={this.closeScanner}
        />
      );
    }

    // Show duplicate VIN warning
    if (this.state.showDuplicateVin) {
      return (
        <div className="vin-step">
          <h4 className="text-center mb-4">Step 1: Verify VIN</h4>
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {this.renderDuplicateVinUI()}
            </div>
          </div>
        </div>
      );
    }

    // Show mismatch UI
    if (this.state.showMismatch) {
      return (
        <div className="vin-step">
          {!this.props.hideStepHeader && <h4 className="text-center mb-4">Step 1: Verify VIN</h4>}
          <div className="row justify-content-center">
            <div className="col-lg-10">
              {this.renderMismatchUI()}
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="vin-step">
        <h4 className="text-center mb-4">Step 1: Verify VIN</h4>

        <div className="row justify-content-center">
          <div className="col-md-8">

            {/* Scanner Button (Mobile Only) */}
            {this.state.isMobile && this.state.scannerAvailable && !this.state.vinConfirmed && (
              <div className="text-center mb-3">
                <button
                  className="btn btn-outline-primary btn-lg btn-block"
                  onClick={this.openScanner}
                  disabled={this.state.loading}
                >
                  <i className="fa-sharp fa-regular fa-barcode mr-2"></i>
                  Scan VIN Barcode
                </button>
              </div>
            )}

            {/* Divider */}
            {this.state.isMobile && this.state.scannerAvailable && !this.state.vinConfirmed && (
              <div className="text-center my-3">
                <small className="text-muted">OR</small>
              </div>
            )}

            {/* VIN Input */}
            <div className="form-group">
              <label htmlFor="vin-input" className="font-weight-bold">
                17-Character Vehicle ID
              </label>
              <input
                id="vin-input"
                type="text"
                className="form-control form-control-lg text-center"
                value={this.state.vin}
                onChange={this.handleVINChange}
                placeholder="Enter vehicle identification number"
                maxLength={17}
                disabled={this.state.loading || this.state.vinConfirmed}
                style={{
                  letterSpacing: '2px',
                  fontFamily: 'monospace',
                  fontSize: '1.2rem'
                }}
              />
            </div>

            {/* Error Display */}
            {this.state.error && (
              <div className="alert alert-danger" role="alert">
                <i className="fa-sharp fa-regular fa-exclamation-triangle mr-2"></i>
                {this.state.error}
              </div>
            )}

            {/* Success Display */}
            {this.state.vinConfirmed && (
              <div className="alert alert-success" role="alert">
                <i className="fa-sharp fa-regular fa-check-circle mr-2"></i>
                Verified successfully! Click NEXT to continue.
              </div>
            )}

            {/* Verify Button */}
            {!this.state.vinConfirmed && (
              <div className="text-center mt-4">
                <button
                  className="btn btn-primary btn-lg"
                  onClick={this.handleVerifyVIN}
                  disabled={this.state.vin.length !== 17 || this.state.loading}
                >
                  {this.state.loading && (
                    <span className="mr-2">
                      <i className="fa-sharp fa-regular fa-refresh fa-spin" />
                    </span>
                  )}
                  VERIFY & CONTINUE
                </button>
              </div>
            )}

            {/* Help Text */}
            <div className="card bg-light mt-4">
              <div className="card-body">
                <h6 className="card-title">
                  <i className="fa-sharp fa-regular fa-info-circle text-primary mr-2"></i>
                  Where to find the VIN:
                </h6>
                <ul className="mb-0 small">
                  <li><strong>Door jamb sticker</strong> (driver's side — best for barcode scanning)</li>
                  <li>Driver's side dashboard (visible through windshield)</li>
                  <li>Vehicle registration or insurance documents</li>
                  <li>Engine block (stamped)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default VINStep;
